<template>
  <HeaderComponent />
  
  <!-- Hero Banner -->
  <section class="w-full mt-[70px] flex items-center justify-center bg-gray-100 border-b border-gray-200">
    <div class="container lg:max-w-[1200px] px-[20px] mx-auto py-4">
      <nav class="flex" aria-label="Breadcrumb">
        <ol class="inline-flex items-center space-x-1 md:space-x-2">
          <li class="inline-flex items-center">
            <router-link to="/" class="text-gray-500 hover:text-orange-600 text-sm font-medium">Accueil</router-link>
          </li>
          <li>
            <div class="flex items-center">
              <svg class="w-3 h-3 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
              </svg>
              <span class="text-orange-600 text-sm font-medium">Boutique</span>
            </div>
          </li>
        </ol>
      </nav>
    </div>
  </section>

  <!-- Main Content -->
  <main class="container lg:max-w-[1200px] mx-auto px-6 py-12">
    <!-- Filter Bar -->
    <div class="bg-white rounded-xl shadow-4xl p-6 mb-12 md:sticky top-[80px] z-20">
      <div class="flex flex-col md:flex-row md:gap-4 items-center">
        <!-- Search -->
        <div class="flex-grow w-full md:w-auto">
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
            <input v-model="searchTerm" @input="debounceSearch" type="text" 
                   class="w-full pl-12 pr-4 py-3 border-0 bg-gray-50 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all" 
                   placeholder="Rechercher un modèle...">
          </div>
        </div>
        
        <!-- Filters -->
        <div class="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <select v-model="selectedCategory" @change="fetchLots" 
                  class="flex-grow md:flex hidden px-4 py-3 bg-gray-50 border-0 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 appearance-none">
            <option value="">Toutes catégories</option>
            <option v-for="category in filters.categories" :key="category" :value="category">
              {{ category }}
            </option>
          </select>
          
          <select v-model="selectedBrand" @change="fetchLots" 
                  class="flex-grow md:flex hidden px-4 py-3 bg-gray-50 border-0 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 appearance-none">
            <option value="">Toutes marques</option>
            <option value="iOS">iOS</option>
            <option value="Android">Android</option>
            <option value="Accessoires">Accessoires</option>
          </select>
          
          <button @click="resetFilters" class="w-[40px] h-[40px] md:flex hidden items-center justify-center bg-gray-100 rounded-[5px] text-gray-600 hover:bg-orange-500 hover:text-white transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Products Grid -->
    <section>
      <div class="flex justify-between items-center mb-8">
        <h2 class="text-2xl font-bold text-gray-800">Nos produits</h2>
        <div class="md:flex hidden items-center space-x-2">
          <span class="text-sm text-gray-500">Trier par :</span>
          <select v-model="sortBy" @change="fetchLots" class="px-3 py-2 bg-gray-50 border-0 rounded-lg outline-none focus:ring-2 focus:ring-orange-500">
            <option value="createdAt">Nouveautés</option>
          </select>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <div v-for="n in 8" :key="n" class="bg-white rounded-xl overflow-hidden shadow-sm animate-pulse">
          <div class="h-60 bg-gray-200"></div>
          <div class="p-4 space-y-3">
            <div class="h-4 bg-gray-200 rounded w-3/4"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
            <div class="h-6 bg-gray-200 rounded w-1/3"></div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!isLoading && lots.length === 0" class="text-center py-16">
        <div class="mx-auto w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mb-4">
          <svg class="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <h3 class="text-xl font-medium text-gray-900 mb-2">Aucun produit trouvé</h3>
        <p class="text-gray-500 mb-6">Essayez de modifier vos critères de recherche</p>
        <button @click="resetFilters" class="px-6 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors">
          Réinitialiser les filtres
        </button>

        <!-- Proposer une demande quand la recherche ne donne rien -->
        <div v-if="searchTerm" class="mt-10 max-w-md mx-auto bg-orange-50 rounded-xl p-6 text-left">
          <h4 class="font-semibold text-gray-800 mb-2">Vous ne trouvez pas "{{ searchTerm }}" ?</h4>

          <template v-if="authClientStore.isLoggedIn">
            <template v-if="!demandeSubmitted">
              <p class="text-sm text-gray-600 mb-4">Faites-en la demande, nous vous notifierons dès que nous l'aurons trouvé.</p>
              <select v-model="demandeBoutique" class="w-full p-3 mb-3 rounded-lg bg-white border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-orange-500">
                <option value="" disabled>Boutique de retrait</option>
                <option v-for="b in boutiques" :key="b" :value="b">{{ b }}</option>
              </select>
              <p v-if="demandeError" class="text-xs text-red-600 mb-2">{{ demandeError }}</p>
              <button @click="submitDemande" :disabled="isSubmittingDemande || !demandeBoutique" class="w-full py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50">
                <span v-if="!isSubmittingDemande">Faire la demande pour "{{ searchTerm }}"</span>
                <span v-else>Envoi...</span>
              </button>
            </template>
            <p v-else class="text-green-600 font-semibold text-sm flex items-center">
              <i class="fas fa-check-circle mr-2"></i> Demande envoyée ! Suivez-la dans votre espace client.
            </p>
          </template>
          <template v-else>
            <p class="text-sm text-gray-600 mb-4">Connectez-vous pour faire une demande et être notifié dès que nous le trouvons.</p>
            <router-link :to="{ name: 'connexion', query: { redirect: '/boutique' } }" class="inline-block px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors">Se connecter</router-link>
          </template>
        </div>
      </div>

      <!-- Products -->
      <div v-if="!isLoading && lots.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <router-link 
          v-for="lot in lots" 
          :key="lot._id" 
          :to="'/boutique/produits/' + lot._id"
          class="group bg-white relative rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-orange-100"
        >
          <!-- Product Badge -->
          <div v-if="lot.categorie === 'iOS'" class="absolute top-4 left-4 bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded z-10">
            iOS
          </div>
          <div v-else-if="lot.categorie === 'Android'" class="absolute top-4 left-4 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded z-10">
            Android
          </div>
          <div v-else-if="lot.categorie === 'Accessoires'" class="absolute top-4 left-4 bg-teal-600 text-white text-xs font-bold px-2 py-1 rounded z-10">
            Accessoires
          </div>
          
          <!-- Product Image -->
          <div class="relative h-60 bg-gray-50 overflow-hidden">
            <img 
              :src="lot.photos[0] || defaultImage" 
              :alt="lot.name" 
              class="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-110"
              @error="onImageError"
            >
            <!-- Quick View -->
            <div class="absolute inset-0 bg-black bg-opacity-0 flex items-center justify-center opacity-0 group-hover:bg-opacity-20 group-hover:opacity-100 transition-all duration-300">
              <button class="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                Voir détails
              </button>
            </div>
          </div>

          <!-- Product Info -->
          <div class="p-5">
            <h3 class="font-semibold text-lg text-gray-800 mb-1 group-hover:text-orange-500 transition-colors">{{ lot.name }}</h3>
            
            <!-- Capacity -->
            <div class="flex flex-wrap gap-1 my-2">
              <span v-for="capacity in uniqueCapacities(lot)" :key="capacity" 
                    class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                {{ capacity }}
              </span>
            </div>
            
            <!-- Price -->
            <div class="mt-3">
              <div v-if="priceRange(lot).min !== priceRange(lot).max" class="flex items-end">
                <span class="text-sm text-gray-500 mr-1">À partir de</span>
                <span class="text-xl font-bold text-orange-500">{{ formatPrice(priceRange(lot).min) }}</span>
              </div>
              <div v-else class="text-xl font-bold text-orange-500">{{ formatPrice(priceRange(lot).min) }}</div>
            </div>
            
            <!-- Stock Status -->
            <div class="mt-3 flex items-center">
              <span class="text-green-500 text-sm flex items-center">
                <i class="fas fa-check-circle mr-1"></i> En stock
              </span>
              <span class="text-gray-400 text-sm ml-auto">{{ lot.produits.length }} modèles</span>
            </div>
          </div>
        </router-link>
      </div>

      <!-- Pagination -->
      <div v-if="!isLoading && lots.length > 0" class="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="text-sm text-gray-500">
          Page {{ pagination.currentPage }} sur {{ pagination.totalPages }} • {{ pagination.totalLots }} produits
        </div>
        <div class="flex gap-2">
          <button 
            @click="changePage(pagination.currentPage - 1)" 
            :disabled="pagination.currentPage === 1"
            class="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          
          <button 
            v-for="page in visiblePages" 
            :key="page" 
            @click="changePage(page)"
            :class="[
              'w-10 h-10 flex items-center justify-center rounded-lg border font-medium',
              page === pagination.currentPage 
                ? 'bg-orange-500 text-white border-orange-500' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
          >
            {{ page }}
          </button>
          
          <button 
            @click="changePage(pagination.currentPage + 1)" 
            :disabled="pagination.currentPage === pagination.totalPages"
            class="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="mt-24 bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-12">
      <div class="max-w-4xl mx-auto text-center">
        <h2 class="text-3xl font-bold text-gray-800 mb-6">Pourquoi choisir notre boutique ?</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="p-4">
            <div class="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center text-white mb-4 mx-auto">
              <i class="fas fa-shield-alt text-2xl"></i>
            </div>
            <h3 class="font-semibold text-lg mb-2">Service Après Vente 12 mois</h3>
            <p class="text-gray-600">Tous nos produits bénéficient d'un Service Après Vente</p>
          </div>
          <div class="p-4">
            <div class="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center text-white mb-4 mx-auto">
              <i class="fas fa-truck text-2xl"></i>
            </div>
            <h3 class="font-semibold text-lg mb-2">Livraison rapide</h3>
            <p class="text-gray-600">Expédition sous 24h et livraison en 48h</p>
          </div>
          <div class="p-4">
            <div class="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center text-white mb-4 mx-auto">
              <i class="fas fa-headset text-2xl"></i>
            </div>
            <h3 class="font-semibold text-lg mb-2">Support premium</h3>
            <p class="text-gray-600">Notre équipe est disponible 6j/7 pour vous aider</p>
          </div>
        </div>
      </div>
    </section>
  </main>

  <FooterComponent />
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import HeaderComponent from '../components/HeaderComponent.vue'
import FooterComponent from '../components/FooterComponent.vue'
import { useAuthClientStore } from '@/store/useAuthClientStore'

const authClientStore = useAuthClientStore()
const defaultImage = '/img/default.png'

function onImageError(event) {
  event.target.src = defaultImage
}

// State
const selectedCapacity = ref('')
const isLoading = ref(false)
const searchTerm = ref('')
const selectedCategory = ref('')
const selectedBrand = ref('')
const sortBy = ref('createdAt')
const pagination = ref({
  currentPage: 1,
  totalPages: 1,
  totalLots: 0
})

// Data
const lots = ref([])
const filters = ref({
  capacities: [],
  colors: [],
  stocks: [],
  categories: [],
  etats: []
})

// Computed properties
const visiblePages = computed(() => {
  const maxVisible = 5
  const half = Math.floor(maxVisible / 2)
  let start = pagination.value.currentPage - half
  let end = pagination.value.currentPage + half

  if (start < 1) {
    start = 1
    end = Math.min(maxVisible, pagination.value.totalPages)
  }

  if (end > pagination.value.totalPages) {
    end = pagination.value.totalPages
    start = Math.max(1, end - maxVisible + 1)
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})

// Methods
const fetchLots = async () => {
  try {
    isLoading.value = true
    const params = {
      page: pagination.value.currentPage,
      search: searchTerm.value,
      categorie: selectedCategory.value || selectedBrand.value,
      capacities: selectedCapacity.value,
      sortBy: sortBy.value.replace('-', ''),
      sortOrder: sortBy.value.startsWith('-') ? 'desc' : 'asc'
    }

    const response = await axios.get('/api/lots/en-ligne', { params })
    lots.value = response.data.lots
    filters.value = response.data.filters
    pagination.value = {
      currentPage: response.data.pagination.currentPage,
      totalPages: response.data.pagination.totalPages,
      totalLots: response.data.pagination.totalLots
    }
  } catch (error) {
    console.error('Error fetching lots:', error)
  } finally {
    isLoading.value = false
  }
}

const priceRange = (lot) => {
  const prices = lot.produits
    .filter(p => p.isOnline)
    .map(p => p.prix_reduit || p.prix)
    .filter(p => p !== null && !isNaN(p))
  
  if (prices.length === 0) return { min: 0, max: 0 }
  
  return {
    min: Math.min(...prices),
    max: Math.max(...prices)
  }
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA'
}

const totalStock = (lot) => {
  return lot.produits.reduce((sum, p) => sum + (p.stock === 'En stock' ? 1 : 0), 0)
}

const uniqueCapacities = (lot) => {
  const capacities = new Set()
  lot.produits.forEach(p => {
    p.capacities?.forEach(c => capacities.add(c))
  })
  return Array.from(capacities).slice(0, 3) // Limit to 3 capacities
}

let searchTimeout = null
const debounceSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    pagination.value.currentPage = 1
    fetchLots()
  }, 500)
}

// Demande d'un produit introuvable (client connecté uniquement)
const boutiques = ['Calavi', 'Parakou']
const demandeBoutique = ref('')
const isSubmittingDemande = ref(false)
const demandeSubmitted = ref(false)
const demandeError = ref('')

watch(searchTerm, () => {
  demandeSubmitted.value = false
  demandeError.value = ''
})

const submitDemande = async () => {
  if (!demandeBoutique.value) return
  isSubmittingDemande.value = true
  demandeError.value = ''
  try {
    await axios.post('/api/plans', { origine: 'demande', nomDemande: searchTerm.value, boutique: demandeBoutique.value })
    demandeSubmitted.value = true
  } catch (e) {
    demandeError.value = e.response?.data?.error || 'Erreur lors de l\'envoi de la demande.'
  } finally {
    isSubmittingDemande.value = false
  }
}

const resetFilters = () => {
  searchTerm.value = ''
  selectedCategory.value = ''
  selectedBrand.value = ''
  selectedCapacity.value = ''
  sortBy.value = 'createdAt'
  pagination.value.currentPage = 1
  fetchLots()
}

const changePage = (page) => {
  if (page >= 1 && page <= pagination.value.totalPages) {
    pagination.value.currentPage = page
    fetchLots()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Lifecycle
onMounted(() => {
  fetchLots()
})
</script>

<style scoped>
/* Custom transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Image hover effect */
.group:hover img {
  transform: scale(1.05);
}

/* Smooth shadow transitions */
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.hover\:shadow-lg:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Custom animations */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>